// Surfsight Camera Domain Configuration
export const surfsightCamConfig = {
  // Basic domain information
  id: 'surfsight-cam',
  name: 'Surfsight Camera',
  tagline: 'Smart, connected fleet management solutions',
  description: 'Transform your business with the power of video.',
  
  // Assets
  logo: '/assets/logos/surfsight-cam-logo.png',
  whiteLogo: '/assets/logos/surfsight-cam-white-logo.png',
  favicon: '/assets/favicons/surfsight-cam-favicon.png',
  icon: '/assets/icons/surfsight-cam',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
              
  // Shop link
  shopLink: 'https://shop.fleethoster.com/products/dcc07-12',

  // UI Configuration
  variant: 'elegant',
  showFeatures: false,
  showTestimonials: false,
  
  // Content sections
  hero: {
    title: 'Surfsight Camera',
    subtitle: 'An AI-powered commercial dash cam equipped with advanced sensors to help you assess what’s happening on the road and inside the vehicle.',
    features: ['Live streaming', 'A.I.-ready', 'Driver coaching feature', 'Built-in storage and cloud storage']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'Surfsight Camera Gallery',
    subtitle: 'Explore our advanced AI-powered dashcam and fleet video solutions',
    description: 'See how Surfsight Camera transforms fleet safety with smart video technology'
  },

  features: {
    title: 'Discover Surfsight Camera Features',
    subtitle: 'Surfsight Camera can not only help you protect your most valuable assets in motion but are a mechanism for organizational transformation.',
    description: 'Discover Safety-Driven Efficiency.',
    items: [
      {
        title: 'Operational Transparency',
        description: 'See what’s happening on the road and inside the vehicle in real-time.',
        icon: '📺'
      },
      {
        title: 'Streamlined Incident Management',
        description: 'Identify and analyze incidents to reduce risk and improve safety.',
        icon: '🚨'
      },
      {
        title: 'Cost Reduction',
        description: 'Reduce costs by improving efficiency and reducing incidents.',
        icon: '💰'
      },
      {
        title: 'Productivity and Efficiency',
        description: 'Optimize routes and reduce downtime with real-time data.',
        icon: '👨‍💻'
      },
      {
        title: 'Driver Coaching Feature',
        description: 'Coach drivers to improve their driving behavior and reduce incidents.',
        icon: '👨‍🏫'
      },
      {
        title: 'Built-in Storage and Cloud Storage',
        description: 'Store and manage videos with built-in storage and cloud storage.',
        icon: '📊'
      }
    ]
  },
  
  solutions: {
    title: 'Surfsight Advanced Camera Solution',
    subtitle: 'Tailored solution for your business needs',
    description: 'Discover our comprehensive range of solutions designed to optimize your operations and drive business growth by Lytx.',
    sections: [
      {
        title: 'Put Safety in the Driver’s Seat',
        description: 'Every trip your fleet takes matters—to your drivers, your customers, and your business. Lytx® gives you the technology you need to protect them all. With advanced fleet safety solutions powered by cutting-edge artificial intelligence and proven insights, you’ll not only help prevent risk—you’ll transform the way your fleet operates.',
      },
      {
        title: 'GPS Fleet Tracking Software with Video',
        description: 'A contextually rich fleet tracking solution that provides the visibility and actionable insights you need to get more done each day and eliminate wasteful spending.',
      },
      {
        title: 'Effortless ELD Compliance Reporting',
        description: 'Lytx® can help you keep your drivers and fleet compliant with government mandates to avoid violations that waste money and may damage your fleet’s reputation.',
      }
    ]
  },

  testimonials: [
    {
      content: "Surfsight Camera has revolutionized how we manage our fleet. The real-time insights are invaluable for our day-to-day operations.",
      author: "John Thomas",
      role: "Fleet Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      content: "The analytics capabilities have helped us reduce costs and improve efficiency significantly. One of the best investments we've made.",
      author: "Timothy Soh",
      role: "Operations Director",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg"
    }
  ],

  blog: {
    title: 'Surfsight Camera Blog',
    subtitle: 'Latest News and Insights',
    description: 'Stay informed with the latest news and insights in fleet management and industry best practices.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'Surfsight Camera - Advanced Camera for Smart Business',
    description: 'Transform operations with cutting-edge tracking: real-time GPS, fleet management, and business intelligence for optimal performance.',
    keywords: [
      'surfsight camera', 'camera', 'fleet tracking', 'fleet management', 'business intelligence', 'fleet optimization', 'real-time tracking',
      'fleet analytics', 'vehicle tracking', 'fleet monitoring', 'surfsight dashcam', 'Surfsight', 'Lytx', 'surfsight shop', 'surfsight store',
      'fleet solutions', 'fleet intelligence', 'surfsight', 'surfsight camera equipment', 'surfsight camera accessories'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'Surfsight Camera - Advanced Camera for Smart Business',
    ogDescription: 'Advanced tracking solutions for fleet optimization and business intelligence. Real-time insights and performance analytics.',
    ogType: 'website',
    ogUrl: 'https://surfsight.cam',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'Surfsight Camera - Advanced Camera for Smart Business',
    twitterDescription: 'Advanced tracking solutions for fleet optimization and business intelligence. Real-time insights and performance analytics.',
    
    // Robots
    robots: 'index, follow',
    author: 'Surfsight Camera',
    publisher: 'Surfsight Camera',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'Surfsight Camera - Professional Dashcam and Accessories',
        description: 'Optimize operations with advanced tracking solutions, including real-time GPS, fleet management, and business intelligence tools',
        keywords: ['surfsight camera', 'fleet tracking', 'asset management', 'business intelligence', 'fleet optimization']
      },
      solutions: {
        title: 'Fleet Tracking Solutions - Surfsight Camera Services',
        description: 'Comprehensive fleet tracking solutions and asset management services. Real-time monitoring, analytics, and optimization tools.',
        keywords: ['fleet tracking solutions', 'asset management services', 'fleet monitoring', 'tracking analytics']
      },
      blog: {
        title: 'Surfsight Camera News & Insights - Fleet Management Blog',
        description: 'Latest news, insights, and best practices for fleet tracking and asset management. Industry updates and optimization tips.',
        keywords: ['surfsight camera news', 'fleet management insights', 'tracking best practices', 'fleet optimization tips']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
